import posthog from 'posthog-js'
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react'

import {
  AnalyticsIdentifyProperties,
  AnalyticsInterface,
  AnalyticsPageProperties,
  AnalyticsTrackProperties,
} from './analyticsTypes'
import {
  transformPosthogGroupUserProperties,
  transformPosthogIdentifyProperties,
  transformPosthogTrackProperties,
} from './transformers/posthogTransformer'

export class PostHogAnalytics implements AnalyticsInterface {
  private analytics?: typeof posthog

  async load() {
    this.analytics = posthog
  }

  isLoaded() {
    const loaded = this.analytics != null

    if (!loaded && process.env.NODE_ENV === 'development') {
      console.warn('Customer IO analytics not loaded')
    }
    return loaded
  }

  async identifyUser(identifyProperties: AnalyticsIdentifyProperties) {
    if (!this.isLoaded()) return

    const transformedProperties =
      transformPosthogIdentifyProperties(identifyProperties)

    this.analytics.identify(
      transformedProperties.userId,
      transformedProperties.traits
    )

    const groupProperties =
      transformPosthogGroupUserProperties(identifyProperties)

    this.analytics.group(
      'company',
      transformedProperties.traits.company.id,
      groupProperties
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async pageViewed(pageName: string, properties?: AnalyticsPageProperties) {
    if (!this.isLoaded()) return

    // Page track is handled by default in _app.tsx
  }

  async trackEvent(eventName: string, properties?: AnalyticsTrackProperties) {
    if (!this.isLoaded()) return

    const transformedProperties = transformPosthogTrackProperties(
      eventName,
      properties
    )

    this.analytics.capture(
      transformedProperties.event,
      transformedProperties.properties
    )
  }
}

const FEATURE_FLAGS = {
  'overview-dashboard-v2': {
    enabled: true,
    tryText: "We've updated the project overview page!",
    tryButton: 'Try it out',
    backText: 'Want to go back to the old Project Overview page?',
    backButton: 'Go back',
  },
}

type FeatureFlags = typeof FEATURE_FLAGS

export const usePostHogFeatureFlag = <T extends keyof FeatureFlags>(
  flag: string
): FeatureFlags[T] => {
  const enabled = useFeatureFlagEnabled(flag)
  const payload = useFeatureFlagPayload(flag) as FeatureFlags[T]

  if (process.env.NODE_ENV === 'development' && payload === undefined) {
    return FEATURE_FLAGS[flag]
  }

  return { enabled, ...payload }
}
